import React, { useEffect, useRef, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import {
  Scale,
  Briefcase,
  Gavel,
  Calculator,
  Landmark,
  Compass,
  MessageCircle,
  MoreHorizontal,
} from 'lucide-react';
import DealProgressTracker from './DealProgressTracker';
import DocumentsTab from './DocumentsTab';
import MessagesTab from './MessagesTab';
import DirectMessageModal from '../Messages/DirectMessageModal';

const START_TEAM_DM = gql`
  mutation StartTeamDm($team_id: ID!, $other_id: ID!) {
    start_team_dm(deal_team_id: $team_id, other_user_id: $other_id) {
      conversation_id
      errors
    }
  }
`;

/* ─── Constants ────────────────────────────────────────────────────── */

const ROLE_CONFIG = {
  cbv: { label: 'CBV', icon: Scale },
  business_broker: { label: 'BROKER', icon: Briefcase },
  lawyer: { label: 'LAWYER', icon: Gavel },
  accountant: { label: 'ACCOUNTANT', icon: Calculator },
  lender: { label: 'LENDER', icon: Landmark },
  cepa_coach: { label: 'CEPA', icon: Compass },
};

const ALL_ROLES = ['cbv', 'business_broker', 'lawyer', 'accountant', 'lender', 'cepa_coach'];

const STATUS_DOT_COLORS = {
  active: 'bg-emerald-500',
  accepted: 'bg-emerald-500',
  invited: 'bg-[hsl(var(--succeedo-orange))]',
  added: 'bg-blue-500',
  off_platform: 'bg-[hsl(var(--muted-foreground))]/40',
  declined: 'bg-red-400',
};

/* ─── Helpers ──────────────────────────────────────────────────────── */

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/* ─── DealTeamWorkspacePage ────────────────────────────────────────── */

const DealTeamWorkspacePage = ({
  team,
  members = [],
  documents = [],
  viewer,
  currentUserId,
  currentUserName,
  onAddRole,
  onUploadDocument,
  onShareSnapshot,
  onAccessChanged,
  onMarkMilestone,
}) => {
  // Default to all-true when viewer is missing (e.g. legacy data path or
  // pre-fetch). The server is the source of truth — client-side gating is
  // affordance-only, mutations enforce permissions independently.
  const canAddMembers     = viewer?.can_add_members     ?? true;
  const canEditMilestones = viewer?.can_edit_milestones ?? true;
  const canUploadToVault  = viewer?.can_upload_to_vault ?? true;
  const [activeTab, setActiveTab] = useState('progress');

  // DM kebab state
  const [openMenuMemberId, setOpenMenuMemberId] = useState(null);
  const [dm, setDm] = useState(null); // { conversationId, recipientName, recipientSubtitle }
  const menuRef = useRef(null);
  const [startTeamDm, { loading: dmStarting }] = useMutation(START_TEAM_DM);

  // Close kebab menu on outside click
  useEffect(() => {
    if (!openMenuMemberId) return undefined;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuMemberId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openMenuMemberId]);

  const handleStartDm = async (member) => {
    setOpenMenuMemberId(null);
    if (!team?.id || !member?.user_id) return;
    try {
      const { data } = await startTeamDm({
        variables: { team_id: team.id, other_id: member.user_id },
      });
      const errors = data?.start_team_dm?.errors || [];
      if (errors.length) {
        // eslint-disable-next-line no-console
        console.error('start_team_dm errors:', errors);
        return;
      }
      const cid = data?.start_team_dm?.conversation_id;
      if (!cid) return;
      const roleConfig = ROLE_CONFIG[member.role] || { label: member.role.toUpperCase() };
      setDm({
        conversationId: cid,
        recipientName: member.name || member.email || 'Team member',
        recipientSubtitle: roleConfig.label,
      });
      if (window.ahoy) {
        window.ahoy.track('succeedo_team_dm_started', {
          deal_team_id: team.id,
          recipient_user_id: member.user_id,
          role: member.role,
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to start team DM:', e);
    }
  };

  // Can DM iff member is on-platform (has user_id), is active/accepted, and isn't the viewer.
  const canDm = (member) =>
    !!member.user_id &&
    String(member.user_id) !== String(currentUserId) &&
    ['accepted', 'active'].includes(member.status);

  // Track page view
  useEffect(() => {
    if (window.ahoy && team?.id) {
      window.ahoy.track('succeedo_deal_team_workspace_viewed', { deal_team_id: team.id });
    }
  }, [team?.id]);

  const filledRoles = new Set(members.map((m) => m.role));
  const emptyRoles = ALL_ROLES.filter((r) => !filledRoles.has(r));

  return (
    <div className="flex h-[calc(100vh-56px)]">
      {/* Left Panel - Team Members */}
      <div className="w-1/3 border-r border-[hsl(var(--border))] p-6 space-y-6 overflow-y-auto">
        <div>
          <h1 className="font-serif text-[18px] font-medium text-[hsl(var(--foreground))] mb-1">
            {team?.name || 'Deal Team'}
          </h1>
          {team?.description && (
            <p className="text-[12px] text-[hsl(var(--muted-foreground))]">
              {team.description}
            </p>
          )}
        </div>

        {/* Section Label */}
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))]" />
          <span className="font-mono text-[11px] text-[hsl(var(--muted-foreground))] uppercase tracking-wide">
            TEAM MEMBERS
          </span>
        </div>

        {/* Member List */}
        <div className="space-y-2">
          {members.map((member) => {
            const roleConfig = ROLE_CONFIG[member.role] || { label: member.role.toUpperCase() };
            const dotColor = STATUS_DOT_COLORS[member.status] || 'bg-[hsl(var(--muted-foreground))]/40';
            const displayName = member.name || member.email || 'Unnamed member';

            return (
              <div
                key={member.id}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-[hsl(var(--muted))]/50 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-[hsl(var(--muted))] flex items-center justify-center flex-shrink-0">
                  <span className="text-[hsl(var(--foreground))] font-semibold text-[12px]">
                    {getInitials(displayName)}
                  </span>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-[hsl(var(--foreground))] font-medium truncate flex items-center gap-2">
                    {displayName}
                    {member.status === 'off_platform' && (
                      <span className="text-[hsl(var(--muted-foreground))] text-[12px] font-normal">
                        (off-platform)
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase text-[hsl(var(--succeedo-orange))]">
                      {roleConfig.label}
                    </span>
                    <div className={`w-2 h-2 rounded-full ${dotColor}`} />
                  </div>
                </div>

                {canDm(member) ? (
                  <div className="relative" ref={openMenuMemberId === member.id ? menuRef : null}>
                    <button
                      type="button"
                      onClick={() =>
                        setOpenMenuMemberId(openMenuMemberId === member.id ? null : member.id)
                      }
                      className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                      aria-label="Member actions"
                    >
                      <MoreHorizontal className="w-4 h-4 flex-shrink-0" />
                    </button>
                    {openMenuMemberId === member.id && (
                      <div className="absolute right-0 top-6 z-10 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-md shadow-lg py-1 min-w-[180px]">
                        <button
                          type="button"
                          onClick={() => handleStartDm(member)}
                          disabled={dmStarting}
                          className="w-full text-left px-3 py-2 text-[13px] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] disabled:opacity-50 flex items-center gap-2"
                        >
                          <MessageCircle className="w-3.5 h-3.5 text-[hsl(var(--succeedo-orange))]" />
                          Message {member.name?.split(/\s+/)[0] || 'directly'}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <MoreHorizontal className="w-4 h-4 text-[hsl(var(--muted-foreground))]/30 flex-shrink-0" />
                )}
              </div>
            );
          })}

          {/* Empty Roles */}
          {emptyRoles.map((role) => {
            const config = ROLE_CONFIG[role];
            const Icon = config.icon;
            return (
              <div
                key={role}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-[hsl(var(--muted))]/50 transition-colors"
              >
                <div className="w-8 h-8 rounded-full border-2 border-dashed border-[hsl(var(--border))] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-[hsl(var(--muted-foreground))] font-medium">
                    {config.label}
                  </div>
                  {canAddMembers && (
                    <button
                      type="button"
                      onClick={() => onAddRole && onAddRole(role)}
                      className="text-[12px] text-[hsl(var(--succeedo-orange))] hover:underline"
                    >
                      + Add
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right Panel - Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Tabs */}
        <div className="border-b border-[hsl(var(--border))] px-6">
          <div className="flex items-center gap-8">
            {[
              { key: 'progress', label: 'PROGRESS' },
              { key: 'documents', label: 'DOCUMENTS' },
              { key: 'messages', label: 'MESSAGES' },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`font-mono text-[11px] uppercase py-4 transition-colors ${
                  activeTab === tab.key
                    ? 'text-[hsl(var(--foreground))] border-b-2 border-[hsl(var(--succeedo-orange))]'
                    : 'text-[hsl(var(--muted-foreground))]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'progress' && (
            <DealProgressTracker
              team={team}
              members={members}
              progress={team?.progress}
              currentUserId={currentUserId}
              teamOwnerUserId={team?.owner_user_id}
              onMarkMilestone={canEditMilestones ? onMarkMilestone : null}
            />
          )}

          {activeTab === 'documents' && (
            <DocumentsTab
              dealTeamId={team?.id}
              documents={documents}
              canUpload={canUploadToVault}
              onUpload={canUploadToVault ? onUploadDocument : null}
              onShareSnapshot={canUploadToVault ? onShareSnapshot : null}
              currentUserId={currentUserId}
              teamOwnerUserId={team?.owner_user_id}
              onAccessChanged={onAccessChanged}
            />
          )}

          {activeTab === 'messages' && (
            <MessagesTab
              dealTeamId={team?.id}
              conversationId={team?.conversation_id}
              currentUserId={currentUserId}
              currentUserName={currentUserName}
            />
          )}
        </div>
      </div>

      <DirectMessageModal
        isOpen={!!dm}
        onClose={() => setDm(null)}
        conversationId={dm?.conversationId}
        recipientName={dm?.recipientName}
        recipientSubtitle={dm?.recipientSubtitle}
      />
    </div>
  );
};

export default DealTeamWorkspacePage;
